import { useCallback, useMemo } from "react";
import { reorderMailAccounts } from "@/lib/api/accounts";
import {
  reorderAccountIds,
  sortAccountsBySortOrder,
} from "@/lib/mail/accountSortOrder";
import type { MailAccountSummary } from "@/lib/types/mail";

type Options = {
  onApplyOrder?: (orderedIds: number[]) => void;
  onReorderFailed?: () => void;
};

export function useOrderedMailAccounts(accounts: MailAccountSummary[], options: Options = {}) {
  const { onApplyOrder, onReorderFailed } = options;

  const orderedAccounts = useMemo(() => sortAccountsBySortOrder(accounts), [accounts]);

  const moveAccount = useCallback(
    (accountId: number, direction: "up" | "down") => {
      const currentIds = orderedAccounts.map((a) => a.id);
      const nextIds = reorderAccountIds(currentIds, accountId, direction);
      if (nextIds === currentIds || nextIds.join(",") === currentIds.join(",")) {
        return;
      }

      onApplyOrder?.(nextIds);
      void reorderMailAccounts(nextIds).then((res) => {
        if (!res.ok) {
          onReorderFailed?.();
        }
      });
    },
    [onApplyOrder, onReorderFailed, orderedAccounts]
  );

  return { orderedAccounts, moveAccount };
}
